import React from 'react';
import { Alert, Button, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setTenantSalesPreview } from '../../sitePublishing/api';
import { TenantPublishStatus } from '../../sitePublishing/types';

// Preview mode: before any owner signs up, show the business's real site at its
// real address so a prospect can see it. Booking stays off and the site shows a
// "Preview only" banner; visitors can try the booking flow, but nothing is booked.
// It ends automatically when the owner accepts their invitation.
const SalesPreviewCard: React.FC<{ siteId: string; salesPreview: NonNullable<TenantPublishStatus['salesPreview']> }> = ({
  siteId, salesPreview,
}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => setTenantSalesPreview(siteId, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-publish-status', siteId] }),
  });
  const issues = mutation.data?.preview && !mutation.data.preview.published ? mutation.data.preview.issues : [];

  if (!salesPreview.available) {
    return <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Typography variant="h6">Preview mode</Typography>
      <Typography color="text.secondary">
        Preview ended when the owner signed up. The site stays hidden until they launch.
      </Typography>
    </Paper>;
  }

  return <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
      <Typography variant="h6">Preview mode</Typography>
      <FormControlLabel
        control={<Switch checked={salesPreview.enabled} disabled={mutation.isPending}
          onChange={(event) => mutation.mutate(event.target.checked)} />}
        label={salesPreview.enabled ? 'On' : 'Off'}
      />
    </Stack>
    <Typography color="text.secondary" sx={{ mb: salesPreview.enabled ? 1.5 : 0 }}>
      Show this business its real site before they sign up. Anyone with the link can see it, with a
      “Preview only” banner. Visitors can try booking, but nothing is booked. It ends automatically when the owner
      accepts their invitation.
    </Typography>
    {salesPreview.enabled && salesPreview.siteUrl ? (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Button variant="contained" endIcon={<OpenInNew />} href={salesPreview.siteUrl} target="_blank" rel="noopener noreferrer">
          Open site
        </Button>
        <Typography variant="body2" color="text.secondary">
          {salesPreview.siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          {salesPreview.live ? '' : ' · publishing, refresh in a moment'}
        </Typography>
      </Stack>
    ) : null}
    {issues.length ? <Alert severity="warning" sx={{ mt: 1.5 }}>
      The preview isn&apos;t showing yet: {issues.join(' ')} Fix this in Edit starter site and it will update.
    </Alert> : null}
    {mutation.isError ? <Alert severity="error" sx={{ mt: 1.5 }}>{mutation.error.message}</Alert> : null}
  </Paper>;
};

export default SalesPreviewCard;
